import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, ScrollText, Receipt, Users, FileText,
  Bell, Brain, Settings, LogOut, Building2, X, LayoutGrid, ClipboardList, Database, ArrowRight
} from 'lucide-react';

const navSections = [
  {
    label: 'Overview',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'manager'] },
      { to: '/floor-plan', icon: LayoutGrid, label: 'Floor Plan' },
    ],
  },
  {
    label: 'Management',
    items: [
      { to: '/leases', icon: ScrollText, label: 'Leases', excludeRoles: ['chef_rayon'] },
      { to: '/invoices', icon: Receipt, label: 'Invoices', excludeRoles: ['chef_rayon'] },
      { to: '/clients', icon: Users, label: 'Clients' },
      { to: '/commercial-agreement', icon: ClipboardList, label: 'Agreements' },
      { to: '/documents', icon: FileText, label: 'Documents', excludeRoles: ['chef_rayon'] },
      { to: '/notifications', icon: Bell, label: 'Notifications', excludeRoles: ['chef_rayon'] },
    ],
  },
  {
    label: 'System',
    items: [
      { to: '/ai-analyzer', icon: Brain, label: 'AI Analyzer', roles: ['admin', 'manager', 'viewer'] },
      { to: '/admin', icon: Settings, label: 'Admin', roles: ['admin'] },
      { to: '/admin/data-sync', icon: Database, label: 'Data Sync', roles: ['admin'] },
    ],
  },
];

export const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const location = useLocation();

  const handleNavClick = () => {
    if (window.innerWidth < 768) {
      onClose?.();
    }
  };

  return (
    <aside className={`sidebar flex flex-col ${isOpen ? 'open' : ''}`} data-testid="sidebar-nav"
      style={{ background: '#1a1a2e', borderRight: '1px solid rgba(255,255,255,0.08)' }}>
      {/* Logo */}
      <div className="px-5 py-5 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: '#E24B4A' }}>
            <Building2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-white font-bold text-sm tracking-wide">SIMLA TRIOLET</h1>
            <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: 'rgba(255,255,255,0.35)' }}>Manager</span>
          </div>
        </div>
        <button className="md:hidden p-1" style={{ color: 'rgba(255,255,255,0.45)' }}
          onClick={onClose} aria-label="Close menu">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-3 px-3 overflow-y-auto">
        {navSections.map((section) => (
          <div key={section.label} className="mb-3">
            <p className="text-[10px] uppercase tracking-widest font-bold px-3 mb-2"
              style={{ color: 'rgba(255,255,255,0.25)' }}>{section.label}</p>
            <div className="space-y-0.5">
              {section.items.map((item) => {
                if (item.roles && !item.roles.includes(user?.role)) return null;
                if (item.excludeRoles && item.excludeRoles.includes(user?.role)) return null;
                const isActive = location.pathname === item.to;
                return (
                  <NavLink key={item.to} to={item.to} onClick={handleNavClick}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all"
                    style={{
                      color: isActive ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.45)',
                      background: isActive ? 'rgba(255,255,255,0.06)' : 'transparent',
                      borderLeft: isActive ? '3px solid #E24B4A' : '3px solid transparent',
                    }}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                    <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* User section */}
      <div className="p-4" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold"
            style={{ background: 'rgba(255,255,255,0.1)' }}>
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{user?.name}</p>
            <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'rgba(255,255,255,0.35)' }}>{user?.role}</p>
          </div>
        </div>
        <button onClick={() => { logout(); onClose?.(); }}
          className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm transition-all"
          style={{ color: 'rgba(255,255,255,0.45)' }}
          data-testid="logout-btn">
          <LogOut className="w-4 h-4" />
          <span>Sign Out</span>
          <ArrowRight className="w-3 h-3 ml-auto" />
        </button>
      </div>
    </aside>
  );
};
